import { apiBoasVindas } from '@modules/aawz-intelligence-v2/api/apiBoasVindas'
import { criarProvedorDeClipe } from '@modules/aawz-intelligence-v2/components/boas-vindas-falada/provedor-de-clipe'
import { primeiroNome } from '@modules/aawz-intelligence-v2/utils/apresentacao-do-profissional'
import { turnoTemCartaoDeBoasVindas } from '@modules/aawz-intelligence-v2/ui-registry/chave-da-boas-vindas'
import { STORE_PRINCIPAL as STORE } from '@modules/aawz-intelligence-v2/api/chat-event-handlers'

// A FIAÇÃO da boas-vindas do primeiro contato (spec-009, US-2).
//
// O player (`components/boas-vindas-falada/`) e a classe de API
// (`api/apiBoasVindas.js`) vieram prontos; este mixin é só a ligação entre os
// dois e a tela. Ele decide QUANDO mostrar, marca a entrega e entrega o fim à
// US-3 — nada além disso.
//
// ★ **O front NÃO decide se é o primeiro contato: ele pergunta.** A régua (não
// ter marca E não ter conversa anterior) mora no servidor porque a checagem em
// Python não é atômica — quem impede a entrega dupla é a unique do banco, e um
// `if` aqui daria a ilusão da mesma garantia com duas abas abertas. Sem
// resposta (`boasVindas === null`), NÃO se entrega: quem não sabe não entrega,
// e o custo de não mostrar é zero — a próxima sessão pergunta de novo.
//
// ★ **Nada aqui toca o envio da mensagem** (AC-2.5). A leitura sai na
// montagem, em paralelo, e o player aparece por um watcher — `enviarMensagem`
// e `despacharMensagem` seguem byte a byte. É o AC mais fácil de quebrar sem
// perceber, porque quebrar não produz erro nenhum: só uma resposta que demora.
export const viewChatBoasVindasMixin = {
  data() {
    return {
      // ★ A boas-vindas já foi ENTREGUE nesta passagem pela tela: a fala
      // acabou, ou o consultor clicou em "Continuar".
      //
      // ⚠️ **[18/09/2026] Isto COLAPSA o player, não o esconde.** Até esta
      // data ela também o removia da tela, e a apresentação da casa sumia sem
      // deixar rastro — *"a mensagem de boas vindas saiu, quero que fique"*
      // (owner, testando em `/home-ia`). Quem garante "uma vez só" de verdade
      // continua sendo a marca no servidor, e é por isso que tirar este campo
      // de `mostrarBoasVindas` não reabre nada: a próxima sessão pergunta e
      // recebe `deve_entregar: false`.
      boasVindasEncerrada: false,
      /**
       * Esta conversa chegou do HISTÓRICO (já tinha mensagens quando abriu).
       *
       * ★ [owner, 19/09/2026] *"por que, para uma conversa já iniciada dias
       * atrás, o card de Olá Herik fica NO COMEÇO DO CHAT?"*. O `deve_entregar`
       * é do PROFISSIONAL, não da conversa — o servidor responde "entregue" e
       * o bloco entra acima de QUALQUER conversa aberta, inclusive uma de
       * meses atrás.
       *
       * Em produção isso não acontece por sorte: quem já tem conversa anterior
       * recebe `ja_usava_antes` e nunca vê a apresentação. Mas "por sorte" não
       * é régua — a apresentação é do PRIMEIRO CONTATO, e ela no alto de uma
       * conversa antiga é falsa por definição, diga o servidor o que disser.
       */
      conversaVeioDoHistorico: false,
      // ★ **[18/09/2026] O PROVEDOR DO CLIPE — o fio que faltava.**
      //
      // O owner abriu a tela e disse: *"não apareceu meu avatar, foi o modo voz
      // puro — esperava um vídeo meu"*. A causa não era o vendor: os dois
      // componentes já aceitavam `provedorDeClipe` e `criarProvedorDeClipe`
      // já estava pronto e testado — mas ninguém o CONSTRUÍA. A prop chegava
      // `null`, `resolverClipe()` voltava na primeira linha e a tela caía no
      // selo, com ou sem a chave do Viggle configurada no servidor.
      //
      // ★ UM provedor para os DOIS rostos (o player e o avatar da conversa):
      // é o mesmo clipe, e a busca é junta na classe de API — a geração no
      // servidor é paga, e dois GETs por passagem seriam dinheiro do vendor.
      //
      // ⚠️ Em `data()` e não em `computed`: um computed devolveria um objeto
      // NOVO a cada leitura, a prop mudaria de identidade a cada render e o
      // `resolverClipe()` dos filhos recomeçaria sem motivo.
      provedorDoClipe: criarProvedorDeClipe({
        buscar: () => apiBoasVindas.carregarClipe(),
      }),
    }
  },
  computed: {
    /**
     * A boas-vindas é da tela do chat, não do popup.
     *
     * Mesma régua de `superficieDeVozTempoReal`: `widget` e `embedded` são
     * superfícies auxiliares, e receber o roteiro de primeiro contato num
     * painel lateral do Hub seria receber a apresentação da casa pela janela.
     */
    superficieDaBoasVindas() {
      return !this.widget && !this.embedded
    },
    /** `{deve_entregar, motivo, versao_roteiro, ...}` do servidor, ou `null`. */
    boasVindasDoServidor() {
      return this.$store.getters[`${STORE}/boasVindas`] || null
    },
    /**
     * ⚠️ Lido do STORE, e não do computed homônimo da view: o mixin é aplicado
     * em `view-chat.vue`, mas depender do nome de um computed de lá amarra os
     * dois — e este arquivo já existe para ser a régua da boas-vindas sozinho.
     */
    /**
     * O namespace do store DESTA conversa.
     *
     * ⚠️ [20/09/2026] Era `STORE` fixo nos quatro leitores abaixo, e na
     * `/home-ia` — que SEMPRE roda com conversa viva — isso deixava dois
     * invariantes do módulo em silêncio: `houvePrimeiraMensagem` nunca ficava
     * verdadeiro (logo o bloco LEGADO de boas-vindas, o fallback para backend
     * sem o turno de primeiro contato, nunca entrava), e `boasVindasNoTurno`
     * nunca via o cartão — então `apresentacaoFoiPedida` respondia NÃO no
     * turno exato em que a casa pediu a apresentação, e a régua dura do cartão
     * de perfil voltava a valer contra a resposta que ela existe para acolher.
     *
     * ★ `boasVindas` (o veredito do servidor) FICA no STORE fixo de propósito:
     * é fato do PROFISSIONAL, não da conversa.
     */
    nsDaConversa() {
      return this.ns || STORE
    },
    carregandoAsMensagensDaConversa() {
      return Boolean(this.$store.getters[`${this.nsDaConversa}/messagesLoading`])
    },
    houvePrimeiraMensagem() {
      const mensagens = this.$store.getters[`${this.nsDaConversa}/messages`] || []
      return mensagens.some((m) => m && m.role === 'user')
    },
    /**
     * O TURNO já traz a boas-vindas como cartão?
     *
     * ★ **O sinal é a PRESENÇA da parte, não uma bandeira de versão.** O
     * backend passou a resolver o primeiro contato como um turno próprio
     * (`core/boas_vindas_do_servidor.py`) e a marcá-lo com
     * `ui.component = 'plataforma/boas-vindas'`. Perguntar "o backend novo está
     * no ar?" exigiria um flag que alguém teria de manter alinhado dos dois
     * lados; perguntar "esta conversa tem o cartão?" é o próprio fato — e ele
     * chega pelo MESMO canal nas duas vidas do cartão (a parte viva do turno em
     * andamento, e `message.tool_calls[]` na conversa reaberta).
     *
     * ⚠️ Ele lê os DOIS estados de propósito: durante o turno a mensagem ainda
     * não existe, e depois do reload a parte viva já foi descartada. Olhar só
     * um deixaria o bloco antigo conviver com o cartão em metade dos casos.
     */
    boasVindasNoTurno() {
      return turnoTemCartaoDeBoasVindas({
        partes: this.$store.getters[`${this.nsDaConversa}/toolPartsOrdenadas`],
        mensagens: this.$store.getters[`${this.nsDaConversa}/messages`],
      })
    },
    /**
     * A boas-vindas está na tela — pelo caminho ANTIGO, o bloco acima da
     * conversa.
     *
     * ★ **Não pergunta se ela já terminou** (ver `boasVindasEncerrada`): uma
     * vez entregue, ela FICA no alto da conversa, como a primeira coisa que
     * foi dita ali. Quem a tira de cena é o servidor na sessão seguinte.
     *
     * ★ **[18/09/2026] O caminho antigo cede a vez ao CARTÃO, e não morre.**
     * Enquanto houver backend sem o turno de primeiro contato, é este bloco que
     * entrega a apresentação — apagá-lo deixaria aquele profissional sem nada.
     * Mas os dois NÃO podem coexistir: com o cartão desenhado como resposta do
     * turno E o bloco acima da conversa, a mesma mensagem aparece duas vezes na
     * mesma tela, e quem lê não vê "dois componentes" — vê a IA se repetindo.
     * Por isso a régua é exclusão mútua, decidida pela presença da parte.
     */
    mostrarBoasVindas() {
      if (!this.superficieDaBoasVindas) return false
      if (this.boasVindasNoTurno) return false
      // ★ **[18/09/2026] O turno em voo NÃO decide — ele espera.**
      //
      // `houvePrimeiraMensagem` fica verdadeiro no instante em que a bolha
      // otimista do usuário entra, e o cartão do servidor só chega no fim do
      // turno. Nessa janela as três condições abaixo são todas verdadeiras, o
      // bloco antigo PISCA acima do composer e some quando o cartão chega —
      // medido pelo owner: *"renderizou o card do olá acima do prompt e depois
      // sumiu em ms, mas isso é chato para o usuário"*.
      //
      // A pergunta que o bloco responde é "o servidor NÃO entregou o cartão?",
      // e durante o turno ainda não há resposta. Esperar é o único desfecho
      // honesto: quando o turno fecha, ou o cartão veio (e `boasVindasNoTurno`
      // corta acima) ou não veio (e o bloco entra, que é o fallback).
      if (this.turnoEmVoo) return false
      // A apresentação é do PRIMEIRO CONTATO — nunca de uma conversa que já
      // existia. Ver `conversaVeioDoHistorico`.
      if (this.conversaVeioDoHistorico) return false
      if (!this.houvePrimeiraMensagem) return false
      return Boolean(this.boasVindasDoServidor && this.boasVindasDoServidor.deve_entregar)
    },
    /**
     * Quem está do outro lado, lido da SESSÃO — nunca do modelo (N21 da
     * spec-008, AC-2.3). Sem nome, o roteiro sai sem nome: o player trata isso
     * e jamais escreve um placeholder.
     */
    primeiroNomeDaSessao() {
      const usuario = (this.$currentUser && this.$currentUser()) || {}
      return primeiroNome(usuario.nome)
    },
  },
  watch: {
    /**
     * O veredito é tirado no fim da CARGA das mensagens, e não na troca de
     * conversa: ao selecionar, a lista é esvaziada e só depois preenchida —
     * perguntar antes responderia "vazia" para toda conversa, inclusive as
     * antigas. Quando `messagesLoading` cai, o que está na lista é o que o
     * servidor tinha.
     *
     * Conversa NOVA não passa por carga nenhuma (ou carrega vazia), então a
     * flag fica `false` e a apresentação acontece como sempre.
     */
    carregandoAsMensagensDaConversa(agora, antes) {
      if (antes && !agora) this.conversaVeioDoHistorico = this.houvePrimeiraMensagem
    },
  },
  created() {
    // Pergunta cedo para que a resposta já esteja em mãos quando a primeira
    // mensagem sair — sem que o envio dependa dela em momento nenhum.
    if (this.superficieDaBoasVindas) this.perguntarAoServidorSobreBoasVindas()
  },
  methods: {
    /**
     * A pergunta ao servidor, blindada.
     *
     * ⚠️ **Uma leitura de segundo plano NUNCA pode derrubar o chat**, e isso
     * não é zelo teórico: `apiBoasVindas` despacha na store GLOBAL, e onde o
     * módulo `aawz-intelligence-v2` não estiver registrado o `dispatch` LANÇA
     * (`[vuex] unknown action type`) de forma SÍNCRONA, antes de qualquer
     * promise — medido, e derrubava a montagem inteira da view. A boas-vindas
     * é um extra do primeiro contato; a conversa é o produto. Falhou, não se
     * entrega — e a próxima sessão pergunta de novo.
     *
     * Sem `console.warn` de propósito: `tests/unit/setup.js` transforma warn
     * em falha, e o desfecho já fica registrado na store
     * (`boasVindasErro`), que é onde se diagnostica.
     */
    perguntarAoServidorSobreBoasVindas() {
      try {
        const leitura = apiBoasVindas.carregar()
        if (leitura && typeof leitura.catch === 'function') leitura.catch(() => {})
      } catch (erro) {
        // Silêncio deliberado — ver o bloco acima.
      }
    },
    /**
     * O player informou por qual canal a mensagem chegou (`audio`/`texto`) —
     * é isso que vira o registro no servidor.
     *
     * ⚠️ **A falha aqui não desfaz nada.** Quando esta chamada acontece, a
     * boas-vindas JÁ está na tela; se a marcação não gravar, o pior desfecho é
     * a pessoa vê-la de novo numa próxima sessão. Por isso nada é escondido e
     * nenhum snack aparece: um aviso de falha sobre algo que ela não pediu e
     * não pode resolver só quebraria o primeiro contato que acabou de dar
     * certo. O erro fica na store, para diagnóstico (`apiBoasVindas`).
     */
    aoEntregarBoasVindas(evento) {
      const canal = evento && evento.canal
      // Canal ausente não vira chute: o registro existe para medir COMO a
      // mensagem chegou, e um valor inventado o tornaria inútil.
      if (!canal) return Promise.resolve(undefined)
      return apiBoasVindas.marcarEntregue({ canal })
    },
    /**
     * A boas-vindas terminou — pela fala ou pelo clique em "Continuar".
     *
     * É aqui que a US-2 encontra a US-3: COLAPSA o player (ele fica na tela,
     * lido) e chama `aoFimDaBoasVindas`
     * (`view-chat-memoria-do-perfil.mixin.js`), que abre a conversa falada com
     * o avatar. Os dois passos moram no mesmo gesto porque para o consultor é
     * um só: a casa acabou de falar, e agora é a vez dele.
     */
    encerrarBoasVindas() {
      this.boasVindasEncerrada = true
      if (typeof this.aoFimDaBoasVindas === 'function') this.aoFimDaBoasVindas()
    },
  },
}
